/**
 * The wallet's one conversation with the issuer after enrolment.
 *
 * At boot when there is a network and every hour while the app is open, it
 * learns the issuer's clock, refreshes the trust store and the status list,
 * uploads the receipts this phone signed, and renews the credential when it is
 * close to expiry. Each step fails on
 * its own: a phone with no network keeps working with what it already holds,
 * and one endpoint down does not stop the others. Verification never waits on
 * any of this; it reads what the last sync stored.
 */
import {
  ATTR, CLOCK_MOVED_S, LOGOUT_STEP_TIMEOUT_MS, RECEIPT_BATCH, RENEW_BEFORE_S, RENEWAL_GRACE_S,
  SYNC_INTERVAL_MS, SYNC_STALE_S,
} from "./constants";
import { asBufferSource, importPublicKey, signWithDevice, verifyEd25519 } from "./crypto";
import {
  decodeAttributes, decodeCredential, decodeStatusList, historySigningInput, isRevoked,
  logoutSigningInput, portraitSigningInput, renewalSigningInput, statusListSigningInput,
} from "./format";
import { fetchTrustStore } from "./issuer-keys";
import type { StoredStatusList } from "./status";
import * as storage from "./storage";
import type { CredentialState } from "./storage";

/** The storage this module reads and writes, by name, so a test can hand it
 *  a map in memory instead of IndexedDB. */
export type SyncStore = Pick<
  typeof storage,
  | "loadTrustStore" | "saveTrustStore" | "saveClockOffset" | "correctedNow"
  | "saveStatusList" | "saveLastSyncAt" | "loadCredential" | "saveCredential"
  | "loadDeviceKey" | "loadCredentialState" | "saveCredentialState" | "saveCredentialLite"
  | "loadReceiptQueue" | "updateReceiptQueue" | "wipeDevice"
>;

export type RenewalOutcome = "renewed" | "not-due" | "dead" | "failed" | "no-credential";

export interface SyncReport {
  trustStore: boolean;
  clock: boolean;
  statusList: boolean;
  renewal: RenewalOutcome;
  receipts: ReceiptUpload;
}

/** What the issuer said about the receipts sent, summed over the batches.
 *  `complete` is false when a batch went unanswered and the rest wait. */
export interface ReceiptUpload {
  accepted: number;
  duplicates: number;
  rejected: number;
  complete: boolean;
}

/** The issuer's 401 details that no retry fixes. Every other refusal, and
 *  every network error, is tried again at the next sync. */
const DEAD_DETAILS = new Set(["expired-too-long", "revoked", "left-directory"]);

const b64 = (raw: Uint8Array) =>
  btoa(String.fromCharCode(...raw)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const unb64 = (text: string) =>
  Uint8Array.from(atob(text.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));

async function attempt<T>(work: () => Promise<T>, failed: T): Promise<T> {
  try {
    return await work();
  } catch {
    return failed;
  }
}

/** `signal` is how a logout ends a request that would otherwise wait on a
 *  network that never answers. */
async function postJSON(path: string, body?: unknown, signal?: AbortSignal): Promise<Response> {
  return fetch(path, {
    method: "POST",
    signal,
    ...(body === undefined
      ? {}
      : { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
  });
}

/**
 * The status list, only if a key in the trust store signed it. Stored
 * unchecked, a list with every bit clear would un-revoke everyone. The issue
 * time kept is the signed one; the `issued_at` beside it in the response is
 * not covered by the signature.
 */
async function verifiedStatusList(
  raw: Uint8Array, trustStore: Map<number, Uint8Array>,
): Promise<StoredStatusList> {
  const list = decodeStatusList(raw);
  const key = await importPublicKey(trustStore.get(list.kid) ?? new Uint8Array(0));
  const input = statusListSigningInput(list.issuedAt, list.bitmap);
  if (!key || !(await verifyEd25519(key, list.signature, input))) {
    throw new Error("status list signature does not verify");
  }
  return { bitmap: list.bitmap, issuedAt: list.issuedAt };
}

export function expiresAtOf(credentialBytes: Uint8Array): number {
  const expiresAt = decodeAttributes(decodeCredential(credentialBytes).attributesBytes)
    .get(ATTR.EXPIRES_AT);
  if (typeof expiresAt !== "number" || !Number.isInteger(expiresAt)) {
    throw new Error("credential carries no expiry");
  }
  return expiresAt;
}

/**
 * Refuses a renewed credential this wallet could not present: one no trusted
 * key signed, or one bound to another device key. Either would replace the
 * one working credential with a card every verifier refuses.
 */
async function checkRenewed(
  renewed: Uint8Array, trustStore: Map<number, Uint8Array>, deviceKey: CryptoKey,
): Promise<void> {
  const credential = decodeCredential(renewed);
  const issuerKey = await importPublicKey(trustStore.get(credential.kid) ?? new Uint8Array(0));
  if (!issuerKey || !(await verifyEd25519(issuerKey, credential.signature, credential.attributesBytes))) {
    throw new Error("renewed credential is not signed by a trusted key");
  }
  const bound = decodeAttributes(credential.attributesBytes).get(ATTR.DEVICE_KEY);
  const ours = new Uint8Array(await crypto.subtle.exportKey("raw", deviceKey));
  if (!(bound instanceof Uint8Array) || b64(bound) !== b64(ours)) {
    throw new Error("renewed credential names another device key");
  }
  expiresAtOf(renewed);
}

/**
 * `freshList` is the status list this sync fetched and verified, or `null`
 * when that step failed. It is how a wallet learns it was revoked without
 * waiting for a renewal to come due: deactivated from another device, or by
 * the UH, its own bit is set in the list days before it would next ask to
 * renew, and until then its card shows a code every verifier refuses.
 */
async function renew(
  store: SyncStore, freshList: StoredStatusList | null, signal?: AbortSignal,
): Promise<RenewalOutcome> {
  const credentialBytes = await store.loadCredential();
  const pair = await store.loadDeviceKey();
  if (!credentialBytes || !pair) return "no-credential";
  const state = await store.loadCredentialState();
  if (state !== null) return "dead";

  const index = decodeAttributes(decodeCredential(credentialBytes).attributesBytes)
    .get(ATTR.STATUS_INDEX);
  if (freshList && typeof index === "number" && isRevoked(freshList.bitmap, index) === true) {
    await store.saveCredentialState("dead", "revoked");
    return "dead";
  }

  const { now } = await store.correctedNow();
  const phase = credentialPhase(expiresAtOf(credentialBytes), now, null);
  if (phase.phase === "re-enrol") {
    // The issuer would answer `expired-too-long`; there is no need to ask.
    await store.saveCredentialState("dead", "expired-too-long");
    return "dead";
  }
  if (phase.phase === "valid" && phase.secondsLeft >= RENEW_BEFORE_S) return "not-due";

  const offered = await postJSON("/api/renovar/desafio", undefined, signal);
  if (!offered.ok) return "failed";
  const challenge = unb64(String(((await offered.json()) as { challenge?: unknown }).challenge));
  const signature = await signWithDevice(
    pair.privateKey, renewalSigningInput(challenge, credentialBytes),
  );
  const response = await postJSON("/api/renovar", {
    credential: b64(credentialBytes),
    challenge: b64(challenge),
    signature: b64(signature),
  }, signal);
  if (response.status === 401) {
    const detail = String(((await response.json()) as { detail?: unknown }).detail);
    if (!DEAD_DETAILS.has(detail)) return "failed";
    await store.saveCredentialState("dead", detail);
    return "dead";
  }
  if (!response.ok) return "failed";
  const body = (await response.json()) as { credential?: unknown; credential_lite?: unknown };
  const renewed = unb64(String(body.credential));
  const trustStore = await store.loadTrustStore();
  await checkRenewed(renewed, trustStore, pair.publicKey);
  // The errand's credential is held to the same test, but on its own: a lite
  // credential that fails it costs the holder errands until the next renewal,
  // and must not cost them the renewal itself. Written even when absent, so
  // the one from the previous credential does not outlive it.
  const lite = await attempt(async () => {
    if (typeof body.credential_lite !== "string") return null;
    const bytes = unb64(body.credential_lite);
    await checkRenewed(bytes, trustStore, pair.publicKey);
    return bytes;
  }, null);
  await store.saveCredential(renewed);
  await store.saveCredentialLite(lite);
  return "renewed";
}

/**
 * The receipt queue, in batches the issuer takes, under this wallet's current
 * credential.
 *
 * A batch the issuer answered leaves the queue whole: `server/app.py` counts
 * every receipt it was sent as accepted, already held or rejected, and a
 * rejected one failed the signature check against this credential's device
 * key, which no later upload changes. A batch it did not answer stays, and so
 * does everything behind it, until the next sync.
 *
 * Also called by the enrolment form before it replaces the device key, since
 * after that every receipt still queued is signed by a key no credential this
 * phone holds names.
 */
export async function uploadReceipts(
  store: Pick<SyncStore, "loadCredential" | "loadReceiptQueue" | "updateReceiptQueue"> = storage,
  signal?: AbortSignal,
): Promise<ReceiptUpload> {
  const upload: ReceiptUpload = { accepted: 0, duplicates: 0, rejected: 0, complete: false };
  const credentialBytes = await store.loadCredential();
  for (;;) {
    const batch = (await store.loadReceiptQueue()).slice(0, RECEIPT_BATCH);
    if (batch.length === 0) return { ...upload, complete: true };
    if (!credentialBytes) return upload;
    let counts: Partial<Record<"accepted" | "duplicates" | "rejected", unknown>>;
    try {
      const response = await postJSON("/api/recibos", {
        credential: b64(credentialBytes), receipts: batch,
      }, signal);
      if (!response.ok) return upload;
      counts = await response.json();
    } catch {
      return upload;
    }
    upload.accepted += Number(counts.accepted) || 0;
    upload.duplicates += Number(counts.duplicates) || 0;
    upload.rejected += Number(counts.rejected) || 0;
    // By value, not by position: a scan while the batch was in flight appended
    // a receipt this batch does not hold, and the cap may have dropped some.
    const sent = new Set(batch);
    await store.updateReceiptQueue((queue) => queue.filter((receipt) => !sent.has(receipt)));
  }
}

export interface HistoryEntry {
  direction: "verified-me" | "i-verified";
  at: number;
  other_uid: string;
  other_name: string;
  result: string;
  mode: number;
  note: string;
}

export type HistoryAnswer =
  | { ok: true; entries: HistoryEntry[] }
  | { ok: false; failure: "offline" | "refused" };

/**
 * This holder's history from the issuer: a challenge, signed by the device
 * key under the history separator, then the entries, newest first. `offline`
 * when a request never reached the issuer, `refused` when it answered no.
 */
export async function fetchHistory(
  store: Pick<SyncStore, "loadCredential" | "loadDeviceKey"> = storage,
): Promise<HistoryAnswer> {
  const credentialBytes = await store.loadCredential();
  const pair = await store.loadDeviceKey();
  if (!credentialBytes || !pair) return { ok: false, failure: "refused" };
  let offered: Response;
  try {
    offered = await postJSON("/api/historial/desafio");
  } catch {
    return { ok: false, failure: "offline" };
  }
  if (!offered.ok) return { ok: false, failure: "refused" };
  const challenge = unb64(String(((await offered.json()) as { challenge?: unknown }).challenge));
  const signature = await signWithDevice(
    pair.privateKey, historySigningInput(challenge, credentialBytes),
  );
  let response: Response;
  try {
    response = await postJSON("/api/historial", {
      credential: b64(credentialBytes),
      challenge: b64(challenge),
      signature: b64(signature),
    });
  } catch {
    return { ok: false, failure: "offline" };
  }
  if (!response.ok) return { ok: false, failure: "refused" };
  const body = (await response.json()) as { entries?: HistoryEntry[] };
  return { ok: true, entries: body.entries ?? [] };
}

export async function syncOnce(
  store: SyncStore = storage, signal?: AbortSignal,
): Promise<SyncReport> {
  const trustStore = await attempt(async () => {
    await store.saveTrustStore(await fetchTrustStore(signal));
    return true;
  }, false);

  let clock = false;
  let freshList: StoredStatusList | null = null;
  try {
    const response = await fetch("/api/estado", { signal });
    if (!response.ok) throw new Error(`/api/estado answered ${response.status}`);
    // The same reading enrolment takes: the response's `Date` header against
    // the device clock at the moment it arrived.
    const deviceNow = Date.now() / 1000;
    const serverSeconds = Date.parse(response.headers.get("date") ?? "") / 1000;
    if (!Number.isNaN(serverSeconds)) {
      await store.saveClockOffset(Math.round(serverSeconds - deviceNow));
      await store.saveLastSyncAt(Math.floor(deviceNow));
      clock = true;
    }
    // Against the trust store as stored, which is the fresh one when
    // `/api/clave` answered and the enrolment one when it did not.
    const body = (await response.json()) as { status_list?: unknown };
    const list = await verifiedStatusList(
      unb64(String(body.status_list)), await store.loadTrustStore(),
    );
    await store.saveStatusList(list);
    freshList = list;
  } catch {
    /* no network, or a list that does not verify: keep the one stored */
  }

  // Before the renewal: a renewal that finds the credential dead sends the
  // holder to enrolment, which replaces the device key these are signed by.
  const receipts = await attempt(
    () => uploadReceipts(store, signal),
    { accepted: 0, duplicates: 0, rejected: 0, complete: false } as ReceiptUpload,
  );
  // After the clock step, so "three days left" is measured on the issuer's
  // clock rather than on a device clock that may be days off.
  const renewal = await attempt(() => renew(store, freshList, signal), "failed" as RenewalOutcome);
  return { trustStore, clock, statusList: freshList !== null, renewal, receipts };
}

/** Whether the issuer confirmed it revoked the credential before the wipe. */
export type LogoutOutcome = "revoked" | "unrevoked";

/**
 * Asks the issuer to revoke this device's credential: a challenge, signed by
 * the device key under the logout separator. `true` only on the issuer's own
 * `revoked: true`; every refusal and every network error is `false`.
 */
async function revokeOnIssuer(
  store: Pick<SyncStore, "loadCredential" | "loadDeviceKey">, signal: AbortSignal,
): Promise<boolean> {
  const credentialBytes = await store.loadCredential();
  const pair = await store.loadDeviceKey();
  if (!credentialBytes || !pair) return false;
  const offered = await postJSON("/api/baja/desafio", undefined, signal);
  if (!offered.ok) return false;
  const challenge = unb64(String(((await offered.json()) as { challenge?: unknown }).challenge));
  const signature = await signWithDevice(
    pair.privateKey, logoutSigningInput(challenge, credentialBytes),
  );
  const response = await postJSON("/api/baja", {
    credential: b64(credentialBytes),
    challenge: b64(challenge),
    signature: b64(signature),
  }, signal);
  if (!response.ok) return false;
  return ((await response.json()) as { revoked?: unknown }).revoked === true;
}

/**
 * Logging out of this device: upload what is owed, have the issuer revoke the
 * credential, and wipe everything the application stored.
 *
 * The receipts go first because nothing can upload them afterwards: they are
 * signed by the key the wipe deletes and sent under the credential it deletes
 * with it. The wipe waits on neither step: offline, or refused, the credential stays
 * unrevoked until it expires, and nobody can present it without the key this
 * deletes. Each step gets its own time limit, so a network that accepts a
 * connection and never answers cannot keep the identity on the device.
 *
 * The caller stops the sync first (`startSync`'s return), or a sync still in
 * flight writes into the store after it was emptied.
 */
export type PortraitOutcome = "uploaded" | "offline" | "refused" | "unreadable";

/** Base64url in slices: the spread in `b64` passes every byte as an argument,
 *  and a photograph has more bytes than a call takes arguments. */
function b64Large(raw: Uint8Array): string {
  let binary = "";
  for (let start = 0; start < raw.length; start += 0x8000) {
    binary += String.fromCharCode(...raw.subarray(start, start + 0x8000));
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Put up `picture` as this person's face, and keep the credential the issuer
 * answers with, which carries it. The issuer checks it the way it checks a
 * renewal and answers the same way, so it is held to the renewal's test here
 * too before it replaces the one this phone has.
 *
 * `offline` is a network that did not answer; `unreadable` is a picture the
 * issuer could not read or would not take; `refused` is everything else, a
 * credential it would not renew included, which the next sync reports in its
 * own terms.
 */
export async function uploadPortrait(
  picture: Uint8Array,
  store: Pick<
    SyncStore,
    "loadCredential" | "loadDeviceKey" | "loadTrustStore" | "saveCredential" | "saveCredentialLite"
  > = storage,
): Promise<PortraitOutcome> {
  const credentialBytes = await store.loadCredential();
  const pair = await store.loadDeviceKey();
  if (!credentialBytes || !pair) return "refused";
  let response: Response;
  try {
    const offered = await postJSON("/api/foto/desafio");
    if (!offered.ok) return "refused";
    const challenge = unb64(String(((await offered.json()) as { challenge?: unknown }).challenge));
    const digest = new Uint8Array(
      await crypto.subtle.digest("SHA-256", asBufferSource(picture)),
    );
    const signature = await signWithDevice(
      pair.privateKey, portraitSigningInput(challenge, credentialBytes, digest),
    );
    response = await postJSON("/api/foto", {
      credential: b64(credentialBytes),
      challenge: b64(challenge),
      signature: b64(signature),
      photo: b64Large(picture),
    });
  } catch {
    return "offline";
  }
  if (response.status === 400 || response.status === 413) return "unreadable";
  if (!response.ok) return "refused";
  const body = (await response.json()) as { credential?: unknown; credential_lite?: unknown };
  const renewed = unb64(String(body.credential));
  const trustStore = await store.loadTrustStore();
  await checkRenewed(renewed, trustStore, pair.publicKey);
  const lite = await attempt(async () => {
    if (typeof body.credential_lite !== "string") return null;
    const bytes = unb64(body.credential_lite);
    await checkRenewed(bytes, trustStore, pair.publicKey);
    return bytes;
  }, null);
  await store.saveCredential(renewed);
  await store.saveCredentialLite(lite);
  return "uploaded";
}

export async function logout(
  store: Pick<
    SyncStore,
    "loadCredential" | "loadDeviceKey" | "loadReceiptQueue" | "updateReceiptQueue" | "wipeDevice"
  > = storage,
  stepTimeoutMs = LOGOUT_STEP_TIMEOUT_MS,
): Promise<LogoutOutcome> {
  await uploadReceipts(store, AbortSignal.timeout(stepTimeoutMs)).catch(() => undefined);
  const revoked = await attempt(
    () => revokeOnIssuer(store, AbortSignal.timeout(stepTimeoutMs)), false,
  );
  await store.wipeDevice();
  return revoked ? "revoked" : "unrevoked";
}

/**
 * Sync at once and then every hour while the app is open. `onReport` hears
 * each result, so the screen can redraw when the credential was renewed or
 * died.
 *
 * Returns a function that stops it for good: no more ticks, the requests of a
 * sync in flight aborted, and a promise that settles once that sync has
 * finished writing. A logout awaits it before wiping the store, because a
 * sync that outlived the wipe would put a status list, a clock or a renewed
 * credential back into it.
 */
export function startSync(
  onReport: (report: SyncReport) => void, store: SyncStore = storage,
): () => Promise<void> {
  const stopping = new AbortController();
  let inFlight: Promise<void> | null = null;
  const run = async () => {
    try {
      const report = await syncOnce(store, stopping.signal);
      if (!stopping.signal.aborted) onReport(report);
    } catch {
      /* storage failed underneath; the next tick tries again */
    }
  };
  const tick = () => {
    // An hourly tick landing on a sync still waiting on a slow network would
    // ask for a second challenge and renew twice.
    inFlight ??= run().finally(() => {
      inFlight = null;
    });
  };
  tick();
  const timer = setInterval(tick, SYNC_INTERVAL_MS);
  return async () => {
    clearInterval(timer);
    stopping.abort();
    await inFlight;
  };
}

export type CredentialPhase =
  | { phase: "valid"; secondsLeft: number }
  | { phase: "expired" }
  | { phase: "re-enrol" };

/**
 * Where a credential stands on the corrected clock. Expired from the second
 * of expiry, as `verify.ts` counts it; past the issuer's grace period, or once
 * the issuer refused it for good, the holder has to enrol again.
 */
export function credentialPhase(
  expiresAt: number, now: number, state: CredentialState | null,
): CredentialPhase {
  if (state !== null || now - expiresAt >= RENEWAL_GRACE_S) return { phase: "re-enrol" };
  if (expiresAt <= now) return { phase: "expired" };
  return { phase: "valid", secondsLeft: expiresAt - now };
}

export type ClockAlarm = "sync-stale" | "clock-moved";

/**
 * The two clock alarms, from the device clock and the device time of the last
 * sync.
 *
 * Only a clock moved backwards is caught offline: the device time at the last
 * sync is the one fixed point there is, and a clock set earlier than it cannot
 * be honest. A clock moved forwards looks like time passing, and shows up as
 * `sync-stale` once it passes a week. A device that never synced gets neither,
 * because it has no corrected clock at all and the card says so already.
 */
export function clockAlarms(deviceNow: number, lastSyncAt: number | null): ClockAlarm[] {
  if (lastSyncAt === null) return [];
  const alarms: ClockAlarm[] = [];
  if (deviceNow - lastSyncAt > SYNC_STALE_S) alarms.push("sync-stale");
  if (lastSyncAt - deviceNow > CLOCK_MOVED_S) alarms.push("clock-moved");
  return alarms;
}

/** The alarms for this device now, for the card and scan screens. */
export async function currentClockAlarms(): Promise<ClockAlarm[]> {
  return clockAlarms(Math.floor(Date.now() / 1000), await storage.loadLastSyncAt());
}
